import React from "react";
import { generateProgressSteps } from "modules/studentView/constant";

import {
  fetchAllPostPromisedData,
  fetchAllPromisedData,
} from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { getDownloadFileUrl } from "views/pages/manage/edpOps/constant/common";
import CustomChip from "views/pages/manage/common/customChip/CustomChip";
import { endpoint } from "views/pages/manage/common/constant";

import { getApproverLevelFromLogs } from "../admission-advisor-dashboard/utils";
import { formatDateTimeForApi } from "../helper";

export const FILTERS_DEFAULT_STATE = {
  onboardedFromDate: "",
  onboardedToDate: "",
  vendorType: "",
  bu: [],
  plantId: [],
  status: [],
};

export const downloadVendorsRequestsCsv = async ({
  searchBy,
  searchByValue,
  filters,
}) => {
  const payload = buildVendorDataPayload(
    { ...filters, searchBy, searchByValue } || {},
  );
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorManagement.downloadCsv}?offset=0&limit=2000`,
    payload,
    "post",
  );

  // expected: response.data => s3 key
  if (response?.code === 200 && response?.data) {
    await getDownloadFileUrl(response.data);
  }

  return response;
};

export const buildVendorDataPayload = (filters) => {
  const payload = {
    vendorType: filters?.vendorType ? [filters.vendorType] : [],
    businessAreaIds: filters?.bu.length ? filters.bu : filters?.businessAreaIds,
    sapPlantId: filters?.plantId || [],
    status: filters?.status || [],
  };

  const onboardedFromDate = filters?.onboardedFromDate
    ? formatDateTimeForApi(filters?.onboardedFromDate)
    : "";
  const onboardedToDate = filters?.onboardedToDate
    ? formatDateTimeForApi(filters?.onboardedToDate, "end")
    : "";

  if (onboardedFromDate) payload.onboardedFromDate = onboardedFromDate;
  if (onboardedToDate) payload.onboardedToDate = onboardedToDate;

  const searchBy = filters?.searchBy;
  const searchByValue = filters?.searchByValue;
  if (searchBy && searchByValue) {
    payload.searchBy = searchBy;
    payload.searchByValue = searchByValue;
  }

  return payload;
};

export async function fetchList({
  searchBy,
  searchByValue,
  filters,
  offset = 0,
  limit = 50,
  businessAreaIds,
}) {
  const url = `${endpoint.vendorManagement.search}?offset=${offset}&limit=${limit}`;

  const payload = buildVendorDataPayload(
    { ...filters, searchBy, searchByValue, businessAreaIds } || {},
  );
  const response = await fetchAllPostPromisedData(url, payload, "post");

  return response;
}

export const editTransactionStatus = async (transactionId, payload) => {
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorManagement.setTransactionStatus}/${transactionId}`,
    payload,
    "put",
  );

  return response;
};

export const fetchApprovalJourney = async (id, limit = 10, offset = 0) => {
  return await fetchAllPromisedData(
    `${endpoint.vendorManagement.auditLogs}?limit=${limit}&offset=${offset}&item_id=${id}&eventType=VENDOR_DETAILS`,
  );
};

export const isActionEnabledForVendorRow = (row, userRole, vendorLevelsApi) => {
  // Final/terminal statuses
  if (
    !row.status ||
    row.status === "ACTIVE" ||
    row.status === "L4_REJECTED" ||
    row.status.endsWith("_REJECTED")
  ) {
    return false;
  }

  // Map status to required approverLevel
  const statusLevelMap = {
    SUBMITTED: 1,
    L1_APPROVED: 2,
    L2_APPROVED: 3,
    L3_APPROVED: 4,
  };
  const currentLevel = statusLevelMap[row.status];
  if (!currentLevel) return false;

  // userRole must match API response data.roleDispVal
  const { roleDispVal = "", vendorLevels = [] } = vendorLevelsApi || {};
  if (!roleDispVal || !Array.isArray(vendorLevels)) return false;
  if (userRole !== roleDispVal) return false;

  // vendorLevels: array of vendorLevel objects
  return vendorLevels.some((vl) => {
    const buMatch = vl.buCode === row.businessAreaValue;
    // const yearMatch = vl.year === row.academicYearDispValue;
    const vendorTypeMatch = vl.vendorType === "VENDOR_CREATION";
    // Check if approverLevels array contains currentLevel
    const approverLevelMatch = vl.approverLevels.some(
      (al) => al.approverLevel === currentLevel,
    );

    return vendorTypeMatch && buMatch && approverLevelMatch;
  });
};

export const FIND_SUCCESS_MESSAGES = (userId, status, remark) => {
  switch (status) {
    case "L1_APPROVED":
      return `Creation request approved at Level 1 by ${userId}.`;
    case "L2_APPROVED":
      return `Creation request approved at Level 2 by ${userId}.`;
    case "L3_APPROVED":
      return `Creation request approved at Level 3 by ${userId}.`;
    case "L4_APPROVED":
      return `Creation request approved at Level 4 by ${userId}.`;
    case "ACTIVE":
      return `Creation request approved at Final Level by ${userId}.`;
    case "L1_REJECTED":
      return `Creation request rejected at Level 1 by ${userId} due to remark: ${remark}.`;
    case "L2_REJECTED":
      return `Creation request rejected at Level 2 by ${userId} due to remark: ${remark}.`;
    case "L3_REJECTED":
      return `Creation request rejected at Level 3 by ${userId} due to remark: ${remark}.`;
    case "L4_REJECTED":
      return `Creation request rejected at Final Level by ${userId} due do remark: ${remark}.`;
  }
};

export const PAYMENT_APPROVAL_STATUS_OPTIONS = [
  { label: "Submitted", value: "SUBMITTED" },
  { label: "L1 Approved", value: "L1_APPROVED" },
  { label: "L2 Approved", value: "L2_APPROVED" },
  { label: "L3 Approved", value: "L3_APPROVED" },
  { label: "L4 Approved", value: "L4_APPROVED" },
  { label: "Success", value: "Active" },
  { label: "L1 Rejected", value: "L1_REJECTED" },
  { label: "L2 Rejected", value: "L2_REJECTED" },
  { label: "L3 Rejected", value: "L3_REJECTED" },
  { label: "L4 Rejected", value: "L4_REJECTED" },
  { label: "Sap Sync Failed", value: "SAP_SYNC_FAILED" },
];

export const getProgressStepList = (approverLevel = 4) => {
  const maxLevel = Math.min(approverLevel, 4);

  const steps = [{ title: "Request initiated" }];

  for (let i = 1; i <= maxLevel; i++) {
    steps.push({ title: `L${i} Approval` });
  }

  steps.push({ title: "Success" });

  return steps;
};

export const getApprovalStepperSteps = (logs = [], approverLevel = 4) => {
  const calculatedLevel = getApproverLevelFromLogs(logs, approverLevel);

  const eventTitleMap = {
    SUBMITTED: "Request initiated",
    L1_APPROVED: "L1 Approval",
    L1_REJECTED: "L1 Approval",
    L2_APPROVED: "L2 Approval",
    L2_REJECTED: "L2 Approval",
    L3_APPROVED: "L3 Approval",
    L3_REJECTED: "L3 Approval",
    L4_APPROVED: "L4 Approval",
    L4_REJECTED: "L4 Approval",
    ACTIVE: "Success",
  };

  const progressSteps = getProgressStepList(calculatedLevel);

  const normalizedLogs = logs
    .filter((log) => eventTitleMap[log.event])
    .map((log) => {
      let status = "completed";

      if (log.event.includes("REJECTED")) status = "rejected";
      else if (log.event === "ACTIVE") status = "success";
      else if (log.event.includes("APPROVED")) status = "approved";
      else if (log.event === "SUBMITTED") status = "completed";

      return {
        title: eventTitleMap[log.event],
        status,
        updatedOn: log.updatedOn || log.createdOn,
        updatedBy: log.updatedBy || log.createdBy,
        remark: log.newValue || "",
      };
    });

  const generatedSteps = generateProgressSteps(progressSteps, normalizedLogs);

  const lastCompletedIndex = generatedSteps.findLastIndex(
    (step) =>
      step.status === "completed" ||
      step.status === "approved" ||
      step.status === "success",
  );

  if (
    lastCompletedIndex !== -1 &&
    lastCompletedIndex < generatedSteps.length - 1
  ) {
    const nextStep = generatedSteps[lastCompletedIndex + 1];

    if (nextStep.status === "upcoming") {
      generatedSteps[lastCompletedIndex + 1] = {
        ...nextStep,
        status: "in_process",
      };
    }
  }

  return generatedSteps;
};

export const STATUS_BADGE = (status) => {
  const statusBadgeMap = {
    SUBMITTED: "Initiated",
    L1_APPROVED: "L1 Approved",
    L1_REJECTED: "L1 Rejected",
    L2_APPROVED: "L2 Approved",
    L2_REJECTED: "L2 Rejected",
    L3_APPROVED: "L3 Approved",
    L3_REJECTED: "L3 Rejected",
    L4_APPROVED: "L4 Approved",
    L4_REJECTED: "L4 Rejected",
    ACTIVE: "Active",
    INACTIVE: "Inactive",
    Failed: "Inactive",
    Success: "Active",
    SAP_SYNC_FAILED: "SAP Sync Failed",
  };

  const getInfo = () => {
    if (
      status.includes("APPROVED") ||
      status.includes("SUBMITTED") ||
      status.includes("ACTIVE") ||
      status.includes("Success")
    )
      return {
        text: statusBadgeMap[status],
        textColor: "#0B442D",
        bgColor: "#BBE2CE",
      };
    else if (status.includes("PENDING")) {
      const level = status.split("_")[0];
      return {
        text: `Pending at ${level}`,
        textColor: "#705010",
        bgColor: "#FFEDC8",
      };
    } else if (
      status.includes("REJECTED") ||
      status.includes("INACTIVE") ||
      status.includes("Failed")
    )
      return {
        text: statusBadgeMap[status],
        textColor: "#B32306",
        bgColor: "#F9D9D7",
      };
  };

  return <CustomChip {...getInfo()} />;
};
